import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import router from '@/router';
import { ERROR_CODE } from '@/@types/Common';
import { Auth } from 'aws-amplify';
import { vxm } from '@/store';

interface ApiRequestConfig {
  readonly theoryApi: AxiosRequestConfig;
  readonly git: AxiosRequestConfig;
}

const configParam: ApiRequestConfig = {
  theoryApi: {
    baseURL: process.env.VUE_APP_THEORY_API_ACCESS_URL as string | undefined,
    headers: {
      'Accept': process.env.VUE_APP_API_HTTP_HEADER_ACCEPT,
      'Content-Type': process.env.VUE_APP_API_HTTP_HEADER_CONTENT_TYPE,
      'X-Requested-With': process.env.VUE_APP_API_HTTP_HEADER_X_REQUESTED_WITH,
      // withCredentials: true,
    },
  },
  git: {
    baseURL: process.env.VUE_APP_THEORY_GIT_ACCESS_URL as string | undefined,
    headers: {
      'Accept': process.env.VUE_APP_API_HTTP_HEADER_ACCEPT,
      'Content-Type': process.env.VUE_APP_API_HTTP_HEADER_CONTENT_TYPE,
      'X-Requested-With': process.env.VUE_APP_API_HTTP_HEADER_X_REQUESTED_WITH,
      // withCredentials: true,
    },
  }
};

export class TheoryApiAxiosBase {
  private readonly axiosClient: AxiosInstance;
  constructor() {
    this.axiosClient = axios.create(configParam.theoryApi);
    this.axiosClient.interceptors.request.use(async (config) => {
      // クエリパラメータのパーサ
      config.paramsSerializer = (params) => qs.stringify(params, { allowDots: true });
      /** AuthorizationヘッダーにJwtを渡す処理 */
      const bearerToken = await fromCognitoUserPoolBearerToken();
      if (bearerToken) {
        config.headers.Authorization = bearerToken;
      }
      /** x-role-paramヘッダーに組織ID・プロジェクトIDを渡す処理 */
      const organizationId = vxm.theoryApi.authStore.getCurrentOrganizationId;
      const projectId = vxm.theoryApi.authStore.getCurrentProjectId;
      config.headers['x-role-param'] = `{"organizationId":${organizationId},"projectId":${projectId}}`;
      return config;
    });
    this.axiosClient.interceptors.response.use(
      (response) => Promise.resolve(response),
      async (error) => {
        if (error.response) {
          console.error(error.response.data);
          // TODO:暫定対応。
          if (error.response.data.statusCode === ERROR_CODE.PLAN_LIMIT_EXCEPTION) {
            router.push({ name: 'errorPage' });
          } else {
            return Promise.reject(error);
          }
        } else {
          return Promise.reject(error);
        }
      },
    );
  }

  async get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.get(url, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }

  async post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.post(url, data, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }

  async patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.patch(url, data, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }

  async delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.delete(url, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }
}

export class GitApiAxiosBase {
  private readonly axiosClient: AxiosInstance;
  constructor() {
    this.axiosClient = axios.create(configParam.git);
    this.axiosClient.interceptors.request.use(async (config) => {
      // クエリパラメータのパーサ
      config.paramsSerializer = (params) => qs.stringify(params, { allowDots: true });
      /** AuthorizationヘッダーにJwtを渡す処理 */
      const bearerToken = await fromCognitoUserPoolBearerToken();
      if (bearerToken) {
        config.headers.Authorization = bearerToken;
      }
      /** x-role-paramヘッダーにプロジェクトIDを渡す処理 */
      const projectId = vxm.theoryApi.authStore.getCurrentProjectId;
      config.headers['x-role-param'] = `{"projectId":${projectId}}`;
      return config;
    });
    this.axiosClient.interceptors.response.use(
      (response) => Promise.resolve(response),
      async (error) => {
        if (error.response) {
          console.error(error.response.data);
          // TODO:暫定対応。
          if (error.response.data.statusCode === ERROR_CODE.PLAN_LIMIT_EXCEPTION) {
            router.push({ name: 'errorPage' });
          } else {
            return Promise.reject(error);
          }
        } else {
          return Promise.reject(error);
        }
      },
    );
  }

  async get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.get(url, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }

  async post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.post(url, data, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }

  async patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.patch(url, data, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }

  async delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    try {
      return await this.axiosClient.delete(url, config);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data);
      }
      return error;
    }
  }
}

async function fromCognitoUserPoolBearerToken() {
  try {
    let jwt = ''
    const currentUserInfo = await Auth.currentUserInfo();
    if (currentUserInfo) {
      const session = await Auth.currentSession();
      jwt = session.getIdToken().getJwtToken();
    }
    return `Bearer ${jwt}`;
  } catch (error: any) {
    if (error.response) {
      console.error(error.response.data);
    }
    return;
  }
}